<script setup lang="ts">
import { Input } from '@/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/ui/dropdown-menu'
import type { HTMLAttributes } from 'vue'
import { buildLinkValue, detectLinkVariable, linkVariables, type LinkVariable } from '../../constants/linkVariables'

const props = withDefaults(
  defineProps<{
    modelValue: string
    class?: HTMLAttributes['class']
    submitLabel?: string
    autofocus?: boolean
    stacked?: boolean
  }>(),
  {
    autofocus: false,
    stacked: false
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'submit', value: string): void
}>()

const { t } = useI18n()

const buttonLabel = computed(() => props.submitLabel ?? t('global.words.add'))
const wrapperClass = computed(() => props.class)

const initial = detectLinkVariable(props.modelValue)
const selectedVariable = ref<LinkVariable>(initial?.variable ?? linkVariables[0])
const url = ref(initial?.url ?? (props.modelValue && !initial ? props.modelValue : ''))

const inputPlaceholder = computed(() => t(selectedVariable.value.description))

watch(
  () => props.modelValue,
  (next) => {
    const detected = detectLinkVariable(next)
    if (detected) {
      selectedVariable.value = detected.variable
      url.value = detected.url
    } else {
      selectedVariable.value = linkVariables[0]
      url.value = next ?? ''
    }
  }
)

const onSelectVariable = (variable: LinkVariable) => {
  const previous = selectedVariable.value
  selectedVariable.value = variable
  if (!variable.requiresUrl) {
    url.value = ''
    const value = buildLinkValue(variable, '')
    emit('update:modelValue', value)
    emit('submit', value)
    return
  }
  if (!previous.requiresUrl) {
    nextTick(() => inputRef.value?.$el?.focus())
  }
}

const onUrlBlur = () => {
  if (!selectedVariable.value.requiresUrl) return
  emit('update:modelValue', buildLinkValue(selectedVariable.value, url.value))
}

const notification = useNotification()

const inputRef = ref<{ $el: HTMLInputElement } | null>(null)

onMounted(() => {
  if (props.autofocus && selectedVariable.value.requiresUrl) {
    nextTick(() => inputRef.value?.$el?.focus())
  }
})

const submit = () => {
  if (selectedVariable.value.requiresUrl && !url.value) return
  const value = buildLinkValue(selectedVariable.value, url.value)
  emit('update:modelValue', value)
  emit('submit', value)
  notification.create('success', { text: t(selectedVariable.value.successMessage) })
}
</script>

<template>
  <div :class="['w-full', wrapperClass]" @keydown.enter.prevent="submit">
    <div class="flex items-stretch">
      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <Button
            type="button"
            variant="outline"
            class="rounded-r-none gap-1.5 shrink-0 text-foreground focus-visible:ring-0 focus-visible:border-input"
          >
            <BaseIcon :name="selectedVariable.icon" class="size-4 text-foreground" />
            {{ t(selectedVariable.label) }}
            <BaseIcon name="HeroChevronDownIconSolid" class="size-3.5 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" class="w-80 p-1">
          <template v-for="(v, index) in linkVariables" :key="v.key">
            <DropdownMenuSeparator v-if="index > 0" />
            <DropdownMenuItem class="gap-3 p-2.5 rounded-md cursor-pointer" @select="onSelectVariable(v)">
              <BaseIcon :name="v.icon" class="size-4 shrink-0 text-foreground mt-0.5" />
              <div class="flex flex-col gap-0.5 min-w-0">
                <span class="text-sm font-medium text-foreground">{{ t(v.label) }}</span>
                <span class="text-xs text-muted-foreground leading-snug whitespace-normal">
                  {{ t(v.description) }}
                </span>
              </div>
            </DropdownMenuItem>
          </template>
        </DropdownMenuContent>
      </DropdownMenu>

      <Input
        ref="inputRef"
        v-model="url"
        type="url"
        :placeholder="inputPlaceholder"
        :disabled="!selectedVariable.requiresUrl"
        :class="[
          'flex-1 focus-visible:ring-0 focus-visible:border-input',
          stacked ? 'rounded-l-none border-l-0' : 'rounded-none border-l-0 border-r-0'
        ]"
        @blur="onUrlBlur"
      />

      <Button v-if="!stacked" type="button" class="rounded-l-none" @click="submit">
        {{ buttonLabel }}
      </Button>
    </div>

    <Button v-if="stacked" type="button" class="mt-2 w-full" @click="submit">
      {{ buttonLabel }}
    </Button>
  </div>
</template>
